/**
 * 학원 운영홈 — 학원 기본정보 표시 + 항목별 수정
 * status 'pending'이면 화면 흐릿 + "승인 대기중입니다" 오버레이
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoStorefrontOutline, IoChevronForward, IoAdd, IoClose, IoCameraOutline } from 'react-icons/io5';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../utils/imageUtils';
import { storage } from '../../firebase/config';
import { useAcademy } from '../../context/AcademyContext';
import { useAuth } from '../../context/AuthContext';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';
import CompanyFooter from '../../components/common/CompanyFooter';

const FIELD_NAME = { study: '교과 과정', sports: '운동 / 태권도', arts: '음악 / 미술' };
const SELECT_OPTS = {
  field: [{ v: 'study', l: '교과 과정' }, { v: 'sports', l: '운동 / 태권도' }, { v: 'arts', l: '음악 / 미술' }],
  type: [{ v: 'academy', l: '학원' }, { v: 'home', l: '교습소' }, { v: 'personal', l: '개인레슨' }],
};

const FIELDS = [
  { key: 'name', label: '학원 이름', kind: 'text' },
  { key: 'type', label: '형태', kind: 'select' },
  { key: 'subjects', label: '과목', kind: 'chips' },
  { key: 'grades', label: '수업 대상', kind: 'lines' },
  { key: 'location', label: '주소', kind: 'text' },
  { key: 'phone', label: '연락처', kind: 'text' },
  { key: 'kioskPin', label: '출석모드\n해제PIN', kind: 'text' },
  { key: 'hours', label: '영업시간', kind: 'lines' },
  { key: 'shuttle', label: '셔틀버스', kind: 'toggle' },
  { key: 'tuition', label: '학원비', kind: 'pairs' },
  { key: 'hashtags', label: '해시태그', kind: 'chips' },
  { key: 'homepage', label: '홈페이지', kind: 'text' },
  { key: 'instagram', label: '인스타그램', kind: 'text' },
  { key: 'blog', label: '블로그', kind: 'text' },
  { key: 'intro', label: '소개', kind: 'textarea' },
];

const displayValue = (f, a) => {
  const v = a[f.key];
  if (f.kind === 'select') {
    if (f.key === 'field') return FIELD_NAME[v] || '-';
    return v === 'home' ? '교습소' : v === 'personal' ? '개인레슨' : '학원';
  }
  if (f.kind === 'toggle') return v ? '제공' : '미제공';
  if (f.kind === 'chips' || f.kind === 'lines') return v && v.length ? v.join(' · ') : '-';
  if (f.kind === 'pairs') return v && v.length ? v.map((t) => `${t.label} ${t.price}`).join(' · ') : '-';
  return v || '-';
};

/* ── 항목 수정 시트 ─────────────────────────────── */
const EditSheet = ({ field: f, academy, onClose, onSave }) => {
  const [val, setVal] = useState(() => {
    if (f.kind === 'chips' || f.kind === 'lines' || f.kind === 'pairs') return academy[f.key] || [];
    if (f.kind === 'toggle') return !!academy[f.key];
    return academy[f.key] || '';
  });
  const [draft, setDraft] = useState('');
  const [pl, setPl] = useState('');
  const [pp, setPp] = useState('');
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    const v = draft.trim();
    if (!v || val.includes(v)) return;
    setVal([...val, v]);
    setDraft('');
  };
  const addPair = () => {
    if (!pl.trim() || !pp.trim()) return;
    setVal([...val, { label: pl.trim(), price: pp.trim() }]);
    setPl('');
    setPp('');
  };
  const remove = (i) => setVal(val.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try { await onSave(f.key, val); } finally { setSaving(false); }
  };

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <SheetTitle>{f.label} 수정</SheetTitle>

        {f.kind === 'text' && (
          <FInput autoFocus value={val} onChange={(e) => setVal(e.target.value)} />
        )}
        {f.kind === 'textarea' && (
          <FArea autoFocus rows={5} value={val} onChange={(e) => setVal(e.target.value)} />
        )}
        {f.kind === 'select' && (
          <ChoiceRow>
            {SELECT_OPTS[f.key].map((o) => (
              <Choice key={o.v} type="button" $on={val === o.v} onClick={() => setVal(o.v)}>{o.l}</Choice>
            ))}
          </ChoiceRow>
        )}
        {f.kind === 'toggle' && (
          <ChoiceRow>
            <Choice type="button" $on={val === true} onClick={() => setVal(true)}>제공</Choice>
            <Choice type="button" $on={val === false} onClick={() => setVal(false)}>미제공</Choice>
          </ChoiceRow>
        )}
        {(f.kind === 'chips' || f.kind === 'lines') && (
          <>
            <AddRow>
              <FInput
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
              />
              <IconBtn type="button" onClick={addItem}><IoAdd size={20} /></IconBtn>
            </AddRow>
            <ItemList>
              {val.map((v, i) => (
                <Item key={i} type="button" onClick={() => remove(i)}>{v} <IoClose size={13} /></Item>
              ))}
            </ItemList>
          </>
        )}
        {f.kind === 'pairs' && (
          <>
            <AddRow>
              <FInput placeholder="" value={pl} onChange={(e) => setPl(e.target.value)} style={{ flex: 1 }} />
              <FInput placeholder="" value={pp} onChange={(e) => setPp(e.target.value)} style={{ flex: 1 }} />
              <IconBtn type="button" onClick={addPair}><IoAdd size={20} /></IconBtn>
            </AddRow>
            <PairHint>왼쪽: 항목 · 오른쪽: 금액</PairHint>
            <ItemList>
              {val.map((t, i) => (
                <Item key={i} type="button" onClick={() => remove(i)}>{t.label} {t.price} <IoClose size={13} /></Item>
              ))}
            </ItemList>
          </>
        )}

        <SheetActions>
          <CancelBtn type="button" onClick={onClose}>취소</CancelBtn>
          <SaveBtn type="button" disabled={saving} onClick={save}>{saving ? '저장 중...' : '저장'}</SaveBtn>
        </SheetActions>
      </Sheet>
    </Overlay>
  );
};

const BizHomePage = () => {
  const navigate = useNavigate();
  const { operatedAcademy, loading, setMode } = useAcademy();
  const { userData } = useAuth();
  const isDemo = userData?.provider === 'demo';
  const [fresh, setFresh] = useState(null);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const aid = operatedAcademy?.id;

  const reload = useCallback(() => {
    if (aid) academyService.getAcademy(aid).then(setFresh);
  }, [aid]);

  useEffect(() => { reload(); }, [reload]);

  const backToPersonal = () => { setMode('personal'); navigate('/app/home'); };

  const saveField = async (key, value) => {
    const patch = { [key]: value };
    if (key === 'subjects') patch.subject = value[0] || '';
    await academyService.updateAcademy(aid, patch);
    setEditing(null);
    reload();
  };

  const changePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = `academy-photos/${aid}-${Date.now()}.webp`;
      const compressed = await compressImage(file, 400, 0.8);
      const snap = await uploadBytes(storageRef(storage, path), compressed);
      const url = await getDownloadURL(snap.ref);
      await academyService.updateAcademy(aid, { photoUrl: url });
      reload();
    } catch (err) {
      console.error('사진 변경 실패:', err);
      alert('사진 변경에 실패했어요.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading || !userData?.uid) return <BrandLoader />;
  if (!operatedAcademy) {
    // 데모 진입 직후 myAcademies가 아직 안 채워졌을 수 있음 — 잠깐 로더
    if (isDemo) return <BrandLoader />;
    return (
      <Container>
        <Empty>
          <EmptyText>운영 중인 학원이 없어요.</EmptyText>
          <EmptyBtn type="button" onClick={() => navigate('/academy/register')}>학원 신청하기</EmptyBtn>
        </Empty>
      </Container>
    );
  }
  if (!fresh) return <BrandLoader />;

  const a = fresh;
  const pending = a.status !== 'approved';

  return (
    <Container>
      <Inner $blur={pending}>
        <Hello>
          <HelloSub>학원 운영</HelloSub>
          <KioskTip>
            <KioskTipText>아이들의 출석을 태블릿 모드로 관리할 수 있어요</KioskTipText>
            <KioskTipTail />
          </KioskTip>
          <NameRow>
            <HelloName>{a.name}</HelloName>
            <KioskBtn type="button" onClick={() => navigate(`/kiosk/${a.id}`)}>
              출석 전용 모드
            </KioskBtn>
          </NameRow>
          <Tags>
            <Tag>{FIELD_NAME[a.field] || a.field}</Tag>
            <Tag>{a.type === 'home' ? '교습소' : a.type === 'personal' ? '개인레슨' : '학원'}</Tag>
            <Tag $on={!pending}>{pending ? '승인 대기중' : '운영중'}</Tag>
          </Tags>
        </Hello>

        {!isDemo && (
          <PreviewCard
            type="button"
            onClick={() => navigate(`/app/academy/${a.id}`, { state: { subject: a.subject } })}
          >
            <IoStorefrontOutline size={20} />
            <PreviewText>학부모에게 보이는 학원 페이지 미리보기</PreviewText>
            <IoChevronForward size={16} />
          </PreviewCard>
        )}

        <SecLabel>학원 정보</SecLabel>

        <PhotoRow as="label" htmlFor="biz-photo">
          <PhotoThumb $url={a.photoUrl} />
          <PhotoText>
            <PhotoTitle>대표 사진</PhotoTitle>
            <PhotoSub>{uploading ? '업로드 중...' : '탭하여 변경'}</PhotoSub>
          </PhotoText>
          <CamIcon><IoCameraOutline size={18} /></CamIcon>
          <HiddenInput id="biz-photo" type="file" accept="image/*" onChange={changePhoto} disabled={uploading} />
        </PhotoRow>

        {FIELDS.map((f) => (
          <FieldRow key={f.key}>
            <FieldL>{f.label}</FieldL>
            <FieldV>{displayValue(f, a)}</FieldV>
            <EditBtn type="button" onClick={() => setEditing(f)}>수정</EditBtn>
          </FieldRow>
        ))}
      </Inner>

      <CompanyFooter />

      {pending && (
        <PendingOverlay>
          <OverlayBox>
            <OverlayTitle>승인 대기중입니다</OverlayTitle>
            <OverlayText>
              1등급 운영자 확인 후<br />
              학원이 디렉토리에 공개돼요.
            </OverlayText>
            <OverlayBtn type="button" onClick={backToPersonal}>개인 모드로 돌아가기</OverlayBtn>
          </OverlayBox>
        </PendingOverlay>
      )}

      {editing && (
        <EditSheet field={editing} academy={a} onClose={() => setEditing(null)} onSave={saveField} />
      )}
    </Container>
  );
};

export default BizHomePage;

const Container = styled.div`
  position: relative;
  min-height: calc(100vh - 120px);
  max-width: 480px;
  margin: 0 auto;
`;

const Inner = styled.div`
  padding: 22px 16px;
  ${({ $blur }) => $blur && 'filter: blur(4px); pointer-events: none; user-select: none;'}
`;

const Hello = styled.div`
  padding: 4px 2px 16px;
`;

const HelloSub = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 4px;
`;

const HelloName = styled.p`
  font-size: 22px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 0;
  flex: 1;
`;

const KioskBtn = styled.button`
  flex-shrink: 0;
  padding: 7px 13px;
  border-radius: 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const KioskTip = styled.div`
  position: relative;
  margin: 8px 0 13px;
  padding: 10px 13px;
  border-radius: 12px;
  background: #22B07D;
`;

const KioskTipText = styled.p`
  font-size: 12.5px;
  font-weight: 700;
  color: #fff;
`;

const KioskTipTail = styled.span`
  position: absolute;
  right: 46px;
  bottom: -5px;
  width: 12px;
  height: 12px;
  background: #22B07D;
  transform: rotate(45deg);
`;

const Tags = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 10px;
`;

const Tag = styled.span`
  padding: 4px 10px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : '#6B7280')};
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : '#F4F5F7')};
`;

const PreviewCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 15px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
`;

const PreviewText = styled.span`
  flex: 1;
  text-align: left;
  font-size: 13.5px;
  font-weight: 700;
`;

const SecLabel = styled.p`
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 24px 0 10px;
`;

const PhotoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 8px;
  cursor: pointer;
`;

const PhotoThumb = styled.div`
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${({ $url }) => ($url ? `#EDEFF3 url(${$url}) center/cover` : '#EDEFF3')};
`;

const PhotoText = styled.div`
  flex: 1;
`;

const PhotoTitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PhotoSub = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 2px;
`;

const CamIcon = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 2px;
  border-bottom: 1px solid #F0F1F4;
`;

const FieldL = styled.span`
  width: 72px;
  flex-shrink: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  white-space: pre-line;
`;

const FieldV = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditBtn = styled.button`
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 8px;
  background: #F4F5F7;
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const PendingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.45);
`;

const OverlayBox = styled.div`
  text-align: center;
  padding: 28px 30px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.14);
`;

const OverlayTitle = styled.p`
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const OverlayText = styled.p`
  font-size: 13.5px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 10px;
`;

const OverlayBtn = styled.button`
  margin-top: 18px;
  padding: 12px 22px;
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13.5px;
  font-weight: 700;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 100px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EmptyBtn = styled.button`
  padding: 12px 26px;
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
`;

/* ── 수정 시트 ─────────────────────────────────── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 84vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 22px 18px 26px;
`;

const SheetTitle = styled.p`
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 14px;
`;

const FInput = styled.input`
  width: 100%;
  padding: 14px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 16px;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const FArea = styled.textarea`
  width: 100%;
  padding: 14px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 16px;
  resize: vertical;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const ChoiceRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Choice = styled.button`
  flex: 1;
  padding: 14px 0;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : '#F4F5F7')};
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.text.secondary)};
`;

const AddRow = styled.div`
  display: flex;
  gap: 8px;
`;

const IconBtn = styled.button`
  flex-shrink: 0;
  width: 50px;
  border-radius: 11px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PairHint = styled.p`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 6px;
`;

const ItemList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 700;
`;

const SheetActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 22px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  background: #F4F5F7;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SaveBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;
